import React, { useState, useCallback } from 'react';
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Code,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Heading,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    Wrap,
    WrapItem,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import { useAdminCredentials } from './hooks/useAdminCredentials';
import { useAdminWorkspaces } from './hooks/useAdminWorkspaces';

/**
 * Platform administration: provider API keys and which workspaces they pay for.
 *
 * PLATFORM ADMIN ONLY (Steph's option A, 2026-08-03). Not workspace admins --
 * billing is not delegated to users, "even admins who could bill other
 * workspaces". Enforced at the API router; this screen is only reachable behind
 * the same `user?.is_admin` guard as the other admin views.
 *
 * WHAT THIS SCREEN DELIBERATELY CANNOT DO
 * ---------------------------------------
 * There is no way to see a key. No reveal button, no copy action, no masked
 * field that unmasks. The API serves no key material at all -- the admin
 * service never decrypts -- so this is not UI restraint, it is the absence of
 * anything to render. `…last4` identifies which key is configured.
 *
 * The screen states that on its face, because an operator who does not know it
 * will otherwise hunt for the missing "show key" button and conclude something
 * is broken. Same reasoning as the "metadata only" labels on the workspace
 * screen's document tabs.
 *
 * WHAT IT MAKES LEGIBLE
 * ---------------------
 *   * which workspaces each key pays for -- with sharing, that IS the blast
 *     radius of a rotation or a deletion, and it is the admin's real job here
 *   * that an unallocated key costs nothing and does nothing
 *   * that a workspace with no allocation falls back to the instance-wide
 *     environment key, rather than failing
 */

const PROVIDERS = [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'mistral', label: 'Mistral' },
];

const providerLabel = (value) =>
    PROVIDERS.find((p) => p.value === value)?.label || value;

const CredentialManagement = () => {
    const {
        credentials,
        isLoading,
        createCredential,
        rotateCredential,
        renameCredential,
        deleteCredential,
        allocateCredential,
        deallocateCredential,
    } = useAdminCredentials();

    // Reused rather than duplicated: the workspace list already exists, and the
    // allocation dropdown needs exactly the same data.
    const { workspaces } = useAdminWorkspaces();

    const toast = useToast();
    const createModal = useDisclosure();
    const rotateModal = useDisclosure();
    const allocateModal = useDisclosure();
    const renameModal = useDisclosure();

    const [selected, setSelected] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [form, setForm] = useState({ provider: 'anthropic', name: '', apiKey: '' });
    const [newKey, setNewKey] = useState('');
    const [newName, setNewName] = useState('');
    const [allocationWorkspaceId, setAllocationWorkspaceId] = useState('');

    const resetForm = () => setForm({ provider: 'anthropic', name: '', apiKey: '' });

    // Dismissing a modal must clear key material too, not just submitting it.
    // Cancel and the X both routed to the bare onClose, so a pasted-then-
    // cancelled key stayed in state until the modal was reopened (David's review
    // of !36). Wrapped so every exit from these two modals -- submit, cancel,
    // close button, overlay click -- drops the plaintext.
    const closeCreateModal = useCallback(() => {
        resetForm();
        createModal.onClose();
    }, [createModal]);

    const closeRotateModal = useCallback(() => {
        setNewKey('');
        rotateModal.onClose();
    }, [rotateModal]);

    const reportError = useCallback((error) => {
        toast({
            title: 'Could not complete that',
            description: error?.message || 'Unexpected error',
            status: 'error',
            duration: 8000,
            isClosable: true,
        });
    }, [toast]);

    // Both submit handlers clear key material in `finally`, NOT on the success
    // path (David's review of !36). Clearing only on success meant an expected
    // error -- a 409 name clash, or a 503 when CREDENTIAL_ENCRYPTION_KEY is
    // unset -- left the plaintext provider key resident in component state while
    // the admin read the toast and navigated away.
    //
    // The trade, decided deliberately: a failed submit costs the admin their
    // paste. A provider key is one clipboard action away; a live secret sitting
    // in memory after a predictable error is not worth saving that. Every other
    // part of this feature refuses to hold key material longer than it must, and
    // this is the same rule applied to the one place it still lingered.
    const handleCreate = async () => {
        setIsSubmitting(true);
        try {
            await createCredential(form);
            createModal.onClose();
        } catch (error) {
            reportError(error);
        } finally {
            setIsSubmitting(false);
            resetForm();
        }
    };

    const handleRotate = async () => {
        setIsSubmitting(true);
        try {
            await rotateCredential(selected.id, newKey);
            rotateModal.onClose();
        } catch (error) {
            reportError(error);
        } finally {
            setIsSubmitting(false);
            setNewKey('');
        }
    };

    const handleRename = async () => {
        setIsSubmitting(true);
        try {
            await renameCredential(selected.id, newName);
            renameModal.onClose();
        } catch (error) {
            reportError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAllocate = async () => {
        setIsSubmitting(true);
        try {
            await allocateCredential(selected.id, parseInt(allocationWorkspaceId, 10));
            setAllocationWorkspaceId('');
            allocateModal.onClose();
        } catch (error) {
            reportError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (credential) => {
        // No confirm dialog for the in-use case: the server refuses it outright
        // and explains which workspaces would break. A confirm would imply the
        // operator can override, and deliberately they cannot.
        try {
            await deleteCredential(credential.id);
            toast({
                title: 'Credential deleted',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            reportError(error);
        }
    };

    const handleDeallocate = async (credentialId, workspaceId) => {
        try {
            await deallocateCredential(credentialId, workspaceId);
        } catch (error) {
            reportError(error);
        }
    };

    // Workspaces that could still receive THIS credential: excludes ones that
    // already hold it. A workspace holding a DIFFERENT key for the same provider
    // is deliberately still listed -- the server explains the conflict and how
    // to swap, which is more useful than a silently short dropdown.
    const availableWorkspaces = (credential) => {
        const taken = new Set((credential?.workspaces || []).map((w) => w.id));
        return workspaces.filter((w) => !taken.has(w.id));
    };

    return (
        <Box p={6} maxW="1200px" mx="auto">
            <Flex justify="space-between" align="center" mb={2}>
                <Heading size="lg">Provider credentials</Heading>
                <Button
                    leftIcon={<AddIcon />}
                    colorScheme="brand"
                    onClick={() => {
                        resetForm();
                        createModal.onOpen();
                    }}
                >
                    Add credential
                </Button>
            </Flex>

            <Text color="text.secondary" fontSize="sm" mb={4}>
                API keys are encrypted at rest and allocated to workspaces. A
                workspace with no credential for a provider falls back to this
                instance&apos;s environment key.
            </Text>

            {/*
              * Stated up front, deliberately. An operator looking for a "show
              * key" button needs to know it does not exist by design, or they
              * will assume the screen is unfinished.
              */}
            <Alert status="info" borderRadius="md" mb={6} fontSize="sm">
                <AlertIcon />
                <Box>
                    <Text fontWeight="600">Keys cannot be read back.</Text>
                    <Text>
                        Once saved, a key is never shown again — not here, not
                        anywhere. Only the last four characters are kept so you
                        can tell keys apart. If you lose a key, replace it with{' '}
                        <strong>Replace key</strong>; you do not need to
                        re-allocate it afterwards.
                    </Text>
                </Box>
            </Alert>

            {isLoading && credentials.length === 0 ? (
                <Flex justify="center" py={10}>
                    <Spinner size="lg" color="brand.500" />
                </Flex>
            ) : credentials.length === 0 ? (
                <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="600">No credentials yet.</Text>
                        <Text fontSize="sm">
                            Every workspace is currently using this
                            instance&apos;s environment keys. That still works —
                            add a credential when you want a workspace billed
                            separately.
                        </Text>
                    </Box>
                </Alert>
            ) : (
                <Table size="sm" variant="simple">
                    <Thead>
                        <Tr>
                            <Th>Provider</Th>
                            <Th>Name</Th>
                            <Th>Key</Th>
                            <Th>Used by</Th>
                            <Th isNumeric>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {credentials.map((credential) => (
                            <Tr key={credential.id}>
                                <Td>
                                    <Badge colorScheme="purple">
                                        {providerLabel(credential.provider)}
                                    </Badge>
                                </Td>
                                <Td fontWeight="500">{credential.name}</Td>
                                <Td>
                                    <Tooltip
                                        label="Only the last four characters are stored for identification"
                                        placement="top"
                                        hasArrow
                                    >
                                        <Code fontSize="xs">
                                            …{credential.last_4 || '????'}
                                        </Code>
                                    </Tooltip>
                                    {credential.key_version > 1 && (
                                        <Badge ml={2} fontSize="xs" colorScheme="gray">
                                            v{credential.key_version}
                                        </Badge>
                                    )}
                                </Td>
                                <Td>
                                    {credential.allocation_count === 0 ? (
                                        <Text fontSize="xs" color="text.secondary">
                                            not allocated — unused
                                        </Text>
                                    ) : (
                                        <Wrap spacing={1}>
                                            {credential.workspaces.map((w) => (
                                                <WrapItem key={w.id}>
                                                    <Badge
                                                        colorScheme="green"
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        {w.name}
                                                        <IconButton
                                                            aria-label={`Remove ${w.name}`}
                                                            icon={<DeleteIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            onClick={() =>
                                                                handleDeallocate(
                                                                    credential.id,
                                                                    w.id
                                                                )
                                                            }
                                                        />
                                                    </Badge>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    )}
                                </Td>
                                <Td isNumeric>
                                    <HStack spacing={1} justify="flex-end">
                                        <Tooltip label="Allocate to a workspace" hasArrow>
                                            <IconButton
                                                aria-label="Allocate"
                                                icon={<AddIcon />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setSelected(credential);
                                                    setAllocationWorkspaceId('');
                                                    allocateModal.onOpen();
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Rename" hasArrow>
                                            <IconButton
                                                aria-label="Rename"
                                                icon={<EditIcon />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setSelected(credential);
                                                    setNewName(credential.name);
                                                    renameModal.onOpen();
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Replace key" hasArrow>
                                            <IconButton
                                                aria-label="Replace key"
                                                icon={<RepeatIcon />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setSelected(credential);
                                                    setNewKey('');
                                                    rotateModal.onOpen();
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip
                                            label={
                                                credential.allocation_count > 0
                                                    ? 'Remove all allocations first'
                                                    : 'Delete'
                                            }
                                            hasArrow
                                        >
                                            <IconButton
                                                aria-label="Delete"
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => handleDelete(credential)}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}

            {/* -- create ---------------------------------------------------- */}
            <Modal isOpen={createModal.isOpen} onClose={closeCreateModal} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add credential</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Provider</FormLabel>
                                <Select
                                    value={form.provider}
                                    onChange={(e) =>
                                        setForm({ ...form, provider: e.target.value })
                                    }
                                >
                                    {PROVIDERS.map((p) => (
                                        <option key={p.value} value={p.value}>
                                            {p.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Name</FormLabel>
                                <Input
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({ ...form, name: e.target.value })
                                    }
                                    placeholder="e.g. main, client-lmoc, dev"
                                />
                                <FormHelperText>
                                    How you will recognise this key later. The last
                                    four characters alone are not much help once you
                                    have several.
                                </FormHelperText>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>API key</FormLabel>
                                <Input
                                    type="password"
                                    value={form.apiKey}
                                    onChange={(e) =>
                                        setForm({ ...form, apiKey: e.target.value })
                                    }
                                    placeholder="sk-…"
                                    autoComplete="off"
                                />
                                <FormHelperText>
                                    Encrypted immediately and never displayed again.
                                    Keep your own copy if the provider does not let
                                    you retrieve it.
                                </FormHelperText>
                            </FormControl>

                            <Alert status="info" fontSize="sm" borderRadius="md">
                                <AlertIcon />
                                Saving does not put this key in use. Allocate it to a
                                workspace afterwards.
                            </Alert>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={closeCreateModal}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleCreate}
                            isLoading={isSubmitting}
                            isDisabled={!form.name.trim() || !form.apiKey.trim()}
                        >
                            Save credential
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- rotate ---------------------------------------------------- */}
            <Modal isOpen={rotateModal.isOpen} onClose={closeRotateModal} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Replace key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" color="text.secondary">
                                {selected?.name} · {providerLabel(selected?.provider)} ·
                                currently …{selected?.last_4}
                            </Text>

                            <FormControl isRequired>
                                <FormLabel>New API key</FormLabel>
                                <Input
                                    type="password"
                                    value={newKey}
                                    onChange={(e) => setNewKey(e.target.value)}
                                    placeholder="sk-…"
                                    autoComplete="off"
                                />
                            </FormControl>

                            {selected?.allocation_count > 0 && (
                                <Alert status="info" fontSize="sm" borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        {selected.allocation_count} workspace(s) will
                                        start using the new key immediately. You do
                                        not need to re-allocate anything.
                                    </Box>
                                </Alert>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={closeRotateModal}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleRotate}
                            isLoading={isSubmitting}
                            isDisabled={!newKey.trim()}
                        >
                            Replace key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- rename ---------------------------------------------------- */}
            <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Rename credential</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                            />
                            <FormHelperText>
                                Renaming does not touch the key or its allocations.
                            </FormHelperText>
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={renameModal.onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleRename}
                            isLoading={isSubmitting}
                            isDisabled={!newName.trim()}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- allocate -------------------------------------------------- */}
            <Modal
                isOpen={allocateModal.isOpen}
                onClose={allocateModal.onClose}
                isCentered
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Allocate to workspace</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" color="text.secondary">
                                {selected?.name} · {providerLabel(selected?.provider)} ·
                                …{selected?.last_4}
                            </Text>

                            <FormControl isRequired>
                                <FormLabel>Workspace</FormLabel>
                                <Select
                                    placeholder="Choose a workspace"
                                    value={allocationWorkspaceId}
                                    onChange={(e) =>
                                        setAllocationWorkspaceId(e.target.value)
                                    }
                                >
                                    {availableWorkspaces(selected).map((w) => (
                                        <option key={w.id} value={w.id}>
                                            {w.name}
                                        </option>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    A workspace can hold one key per provider. If it
                                    already has a different{' '}
                                    {providerLabel(selected?.provider)} key, remove
                                    that allocation first.
                                </FormHelperText>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={allocateModal.onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleAllocate}
                            isLoading={isSubmitting}
                            isDisabled={!allocationWorkspaceId}
                        >
                            Allocate
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default CredentialManagement;
